// components/secure/PayrollTaxTables.js
// Payroll → Tax Tables. Every constant for a year, edited and signed off here, the
// federal withholding brackets, and "Start next year" for the January changeover.
//
// Until this existed the screen was read-only: changing the SUTA rate from the IDES
// letter, or loading next year's figures, meant a developer running a seed script.
// The interlock is unchanged — payroll refuses to run on any unsigned constant — and
// changing a number withdraws its sign-off, so a signature always covers the figure
// that is actually there.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from './Modal';

const th = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500';
const td = 'px-3 py-2 text-sm';
const num = `${td} text-right tabular-nums`;
const btn = 'text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300';
const ghost = 'text-sm px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50';
const input = 'mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm';
const label = 'block text-xs font-medium text-slate-600';
const FILING = [['single', 'Single'], ['married_joint', 'Married filing jointly'], ['head_household', 'Head of household']];
const fmt = (n) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 4 });

export default function PayrollTaxTables({ user }) {
    const api = useApiClient();
    const thisYear = new Date().getFullYear();
    const [year, setYear] = useState(thisYear);
    const [years, setYears] = useState([]);
    const [cfg, setCfg] = useState(null);
    const [brackets, setBrackets] = useState([]);
    const [showBrackets, setShowBrackets] = useState(false);
    const [edit, setEdit] = useState(null);       // a constant being edited
    const [bracket, setBracket] = useState(null); // a bracket being edited / added
    const [adding, setAdding] = useState(null);   // a new constant
    const [error, setError] = useState('');
    const [flash, setFlash] = useState('');
    const [busy, setBusy] = useState(false);
    const broker = !!user?.isManagingBroker;

    const note = (m) => { setFlash(m); setTimeout(() => setFlash(''), 6000); };
    const fail = (e) => setError(e.message || String(e));

    const load = useCallback(async () => {
        setError('');
        try {
            const [ys, c, b] = await Promise.all([
                api.get('/api/payroll/config-years'),
                api.get(`/api/payroll/config/${year}`),
                api.get(`/api/payroll/config/${year}/brackets`),
            ]);
            setYears(ys || []); setCfg(c); setBrackets(b || []);
        } catch (e) { fail(e); }
    }, [api, year]);
    useEffect(() => { load(); }, [load]);

    const run = async (fn) => {
        setBusy(true); setError('');
        try { await fn(); } catch (e) { fail(e); } finally { setBusy(false); }
    };

    const saveConstant = (sign) => run(async () => {
        const r = await api.put(`/api/payroll/config/${year}`, {
            jurisdiction: edit.jurisdiction, item: edit.item, value: edit.value, note: edit.note,
            ...(sign ? { verified_by: true } : {}),
        });
        setEdit(null); note(r.message); load();
    });
    const signOne = (i) => run(async () => {
        const r = await api.put(`/api/payroll/config/${year}`, { jurisdiction: i.jurisdiction, item: i.item, verified_by: true });
        note(r.message); load();
    });
    const unsignOne = (i) => run(async () => {
        const r = await api.put(`/api/payroll/config/${year}`, { jurisdiction: i.jurisdiction, item: i.item, unsign: true });
        note(r.message); load();
    });
    const verifyAll = () => {
        if (!window.confirm(`Sign off every unsigned ${year} tax constant?\n\nThis records you as having checked each one against its source.`)) return;
        run(async () => { const r = await api.post(`/api/payroll/config/${year}/verify-all`, {}); note(r.message); load(); });
    };
    const startYear = (target) => {
        if (!window.confirm(`Start the ${target} tax table from ${target - 1}?\n\nEvery constant and bracket is copied UNSIGNED. Payroll for ${target} will not run until you replace each figure with the ${target} number (SSA wage base, IRS Pub 15-T, IL-700-T, your IDES rate letter) and sign it off.`)) return;
        run(async () => {
            const r = await api.post(`/api/payroll/config/${target}/start`, { from_year: target - 1 });
            note(r.message); setYear(target);
        });
    };
    const addConstant = () => run(async () => {
        const r = await api.post(`/api/payroll/config/${year}/items`, adding);
        setAdding(null); note(r.message); load();
    });
    const saveBracket = () => run(async () => {
        const r = bracket.bracket_id
            ? await api.put(`/api/payroll/brackets/${bracket.bracket_id}`, bracket)
            : await api.post(`/api/payroll/config/${year}/brackets`, bracket);
        setBracket(null); note(r.message); load();
    });
    const removeBracket = (b) => {
        if (!window.confirm('Remove this bracket row?')) return;
        run(async () => { const r = await api.delete(`/api/payroll/brackets/${b.bracket_id}`); note(r.message); load(); });
    };

    const yearList = Array.from(new Set([...years.map(y => y.tax_year), thisYear])).sort();
    const latest = years.length ? Math.max(...years.map(y => y.tax_year)) : null;

    return (
        <div className="space-y-4">
            {error && <div className="px-3 py-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{error}</div>}
            {flash && <div className="px-3 py-2 rounded border border-green-200 bg-green-50 text-green-800 text-sm">{flash}</div>}

            <div className="flex items-center gap-3 flex-wrap">
                <label className="text-sm text-slate-600">Tax year
                    <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="ml-2 border border-slate-300 rounded-md px-2 py-1 text-sm">
                        {yearList.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </label>
                {broker && latest && !years.some(y => y.tax_year === latest + 1) && (
                    <button className={ghost} disabled={busy} onClick={() => startYear(latest + 1)}>
                        Start {latest + 1} from {latest}
                    </button>
                )}
                {broker && cfg?.present && (
                    <button className={ghost} onClick={() => setAdding({ jurisdiction: 'employer', item: '', value: '', note: '' })}>+ Add a constant</button>
                )}
            </div>

            {cfg && !cfg.present && (
                <div className="px-3 py-2 rounded border border-amber-200 bg-amber-50 text-amber-800 text-sm">
                    There is no {year} tax table. {broker && latest
                        ? <>Use <strong>Start {latest + 1} from {latest}</strong> above (it copies the figures unsigned for you to update).</>
                        : 'The managing broker can start one from the previous year.'}
                </div>
            )}

            {cfg?.present && (
                <div className={`px-3 py-2 rounded border text-sm ${cfg.verified
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                    {cfg.verified
                        ? `All ${cfg.year} constants are signed off. Payroll will run normally.`
                        : `Unsigned constants: ${cfg.unverified.join(', ')}. Payroll refuses to run until these are checked, unless run as provisional.`}
                    {!cfg.verified && broker && (
                        <button onClick={verifyAll} className="ml-3 underline font-medium">Sign off all</button>
                    )}
                </div>
            )}

            {cfg?.present && (
                <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200"><tr>
                            <th className={th}>Jurisdiction</th><th className={th}>Item</th>
                            <th className={`${th} text-right`}>Value</th><th className={th}>Signed off</th><th className={th}>Note</th>
                            {broker && <th className={th} />}
                        </tr></thead>
                        <tbody className="divide-y divide-slate-100">
                            {cfg.items.map(i => (
                                <tr key={i.config_id}>
                                    <td className={td}>{i.jurisdiction}</td>
                                    <td className={`${td} font-medium`}>{i.item}</td>
                                    <td className={num}>{fmt(i.value)}</td>
                                    <td className={td}>{i.verified_by
                                        ? <span>{i.verified_by}{i.verified_at ? ` · ${String(i.verified_at).slice(0, 10)}` : ''}</span>
                                        : <span className="text-amber-600">unsigned</span>}</td>
                                    <td className={`${td} text-xs text-slate-500`}>{i.note}</td>
                                    {broker && (
                                        <td className={`${td} whitespace-nowrap text-right space-x-2`}>
                                            <button className="text-blue-700 hover:underline" onClick={() => setEdit({ ...i, value: String(Number(i.value)) })}>Edit</button>
                                            {i.verified_by
                                                ? <button className="text-slate-500 hover:underline" onClick={() => unsignOne(i)}>Withdraw</button>
                                                : <button className="text-green-700 hover:underline" onClick={() => signOne(i)}>Sign off</button>}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {cfg?.present && (
                <div className="bg-white border border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between px-4 py-3">
                        <div className="text-sm text-slate-700">
                            <strong>Federal withholding brackets</strong> — {brackets.length} rows for {year} (IRS Pub 15-T, annual percentage method).
                        </div>
                        <div className="space-x-2">
                            <button className={ghost} onClick={() => setShowBrackets(s => !s)}>{showBrackets ? 'Hide' : 'Show'}</button>
                            {broker && showBrackets && (
                                <button className={ghost} onClick={() => setBracket({ filing_status: 'single', multiple_jobs: 0, income_from: '', base_withhold: '', rate: '' })}>+ Add row</button>
                            )}
                        </div>
                    </div>
                    {showBrackets && (
                        <div className="overflow-x-auto border-t border-slate-200">
                            <table className="w-full">
                                <thead className="bg-slate-50"><tr>
                                    <th className={th}>Filing status</th><th className={th}>Table</th>
                                    <th className={`${th} text-right`}>Annual wage from</th><th className={`${th} text-right`}>Base withholding</th>
                                    <th className={`${th} text-right`}>Rate</th>{broker && <th className={th} />}
                                </tr></thead>
                                <tbody className="divide-y divide-slate-100">
                                    {brackets.map(b => (
                                        <tr key={b.bracket_id}>
                                            <td className={td}>{(FILING.find(f => f[0] === b.filing_status) || [0, b.filing_status])[1]}</td>
                                            <td className={td}>{Number(b.multiple_jobs) ? 'Step 2 box checked' : 'Standard'}</td>
                                            <td className={num}>{fmt(b.income_from)}</td>
                                            <td className={num}>{fmt(b.base_withhold)}</td>
                                            <td className={num}>{(Number(b.rate) * 100).toFixed(2)}%</td>
                                            {broker && (
                                                <td className={`${td} text-right whitespace-nowrap space-x-2`}>
                                                    <button className="text-blue-700 hover:underline" onClick={() => setBracket({ ...b, multiple_jobs: Number(b.multiple_jobs) })}>Edit</button>
                                                    <button className="text-red-600 hover:underline" onClick={() => removeBracket(b)}>Remove</button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <p className="text-xs text-slate-500 px-4 py-2">Changing a bracket withdraws the sign-off on <em>federal.brackets_reviewed</em> (when the year has one), so payroll waits for it to be signed again.</p>
                        </div>
                    )}
                </div>
            )}

            <Modal open={!!edit} onClose={() => setEdit(null)} title={edit ? `${edit.jurisdiction}.${edit.item} — ${year}` : ''}
                footer={<div className="flex gap-2 justify-end w-full">
                    <button className={ghost} onClick={() => setEdit(null)}>Cancel</button>
                    <button className={ghost} disabled={busy} onClick={() => saveConstant(false)}>Save</button>
                    <button className={btn} disabled={busy} onClick={() => saveConstant(true)}>Save and sign off</button>
                </div>}>
                {edit && (
                    <div className="space-y-3">
                        <div><label className={label}>Value</label>
                            <input className={input} value={edit.value} onChange={(e) => setEdit({ ...edit, value: e.target.value })} inputMode="decimal" />
                            <p className="text-xs text-slate-500 mt-1">Rates are fractions (0.062 for 6.2%) unless the item's note says otherwise. Changing the value withdraws any earlier sign-off.</p>
                        </div>
                        <div><label className={label}>Source / note</label>
                            <input className={input} value={edit.note || ''} onChange={(e) => setEdit({ ...edit, note: e.target.value })} placeholder="e.g. IDES rate determination letter dated 15 Dec 2026" />
                        </div>
                    </div>
                )}
            </Modal>

            <Modal open={!!adding} onClose={() => setAdding(null)} title={`Add a ${year} constant`}
                footer={<div className="flex gap-2 justify-end w-full">
                    <button className={ghost} onClick={() => setAdding(null)}>Cancel</button>
                    <button className={btn} disabled={busy} onClick={addConstant}>Add</button>
                </div>}>
                {adding && (
                    <div className="space-y-3">
                        <div><label className={label}>Jurisdiction</label>
                            <input className={input} value={adding.jurisdiction} onChange={(e) => setAdding({ ...adding, jurisdiction: e.target.value.toLowerCase() })} placeholder="federal, illinois, employer" /></div>
                        <div><label className={label}>Item</label>
                            <input className={input} value={adding.item} onChange={(e) => setAdding({ ...adding, item: e.target.value.toLowerCase() })} placeholder="e.g. suta_rate" /></div>
                        <div><label className={label}>Value</label>
                            <input className={input} value={adding.value} onChange={(e) => setAdding({ ...adding, value: e.target.value })} inputMode="decimal" /></div>
                        <div><label className={label}>Source / note</label>
                            <input className={input} value={adding.note} onChange={(e) => setAdding({ ...adding, note: e.target.value })} /></div>
                        <p className="text-xs text-slate-500">The payroll engine only uses items it knows by name; a new item is a record until the software reads it.</p>
                    </div>
                )}
            </Modal>

            <Modal open={!!bracket} onClose={() => setBracket(null)} title={bracket?.bracket_id ? 'Edit bracket' : `Add a ${year} bracket`}
                footer={<div className="flex gap-2 justify-end w-full">
                    <button className={ghost} onClick={() => setBracket(null)}>Cancel</button>
                    <button className={btn} disabled={busy} onClick={saveBracket}>Save</button>
                </div>}>
                {bracket && (
                    <div className="space-y-3">
                        <div><label className={label}>Filing status</label>
                            <select className={input} value={bracket.filing_status} onChange={(e) => setBracket({ ...bracket, filing_status: e.target.value })}>
                                {FILING.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select></div>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!bracket.multiple_jobs}
                            onChange={(e) => setBracket({ ...bracket, multiple_jobs: e.target.checked ? 1 : 0 })} /> Step 2 (multiple jobs) table</label>
                        <div><label className={label}>Annual wage from ($)</label>
                            <input className={input} value={bracket.income_from} onChange={(e) => setBracket({ ...bracket, income_from: e.target.value })} inputMode="decimal" /></div>
                        <div><label className={label}>Base withholding ($)</label>
                            <input className={input} value={bracket.base_withhold} onChange={(e) => setBracket({ ...bracket, base_withhold: e.target.value })} inputMode="decimal" /></div>
                        <div><label className={label}>Rate (fraction — 0.22 for 22%)</label>
                            <input className={input} value={bracket.rate} onChange={(e) => setBracket({ ...bracket, rate: e.target.value })} inputMode="decimal" /></div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
